import { Box, MenuItem, Select, Tooltip, Typography } from "@mui/material";
import { ErrorOutline, ScheduleSend } from "@mui/icons-material";
import React from "react";
import {
  GuestStatus,
  getGuestStatusBackgroundColor,
  GuestStatusOption,
  HostAuthStatus,
} from "src/types";
import { GUEST_STATUS_TO_LABEL } from "src/meetings/props";
import { useHasOrgAdminPermissions } from "src/auth";
import useOverrideStatusRules from "src/meetings/guestStatusSelect/useOverrideStatusRules";
import { useGuestStatusOverride } from "src/mutations/useGuestStatusOverride";
import { useSnackbar } from "src/hooks";
import { guestStatusOptionFromValue } from "src/types/GuestStatus";

interface GuestStatusCellProps {
  status?: GuestStatus;
  meetingId: number;
  guestId?: number;
  meetingStatus: string;
  executeAt?: string | null;
  hostAuthStatus?: HostAuthStatus;
}

export function GuestStatusCell(props: GuestStatusCellProps) {
  const hasOrgAdminPermissions = useHasOrgAdminPermissions();
  const { overridable, overrideOptions } = useOverrideStatusRules();
  const guestStatusOverride = useGuestStatusOverride();
  const [openSnackbar] = useSnackbar();

  if (!props.status) {
    return null;
  }

  const canEdit =
    hasOrgAdminPermissions &&
    props.guestId &&
    overridable({
      fromStatus: props.status,
    });

  // Note: these icons are rendered in the non-editable branch below, which works
  // for all users (including admins) because "staging" is not a key in the
  // override rules, so canEdit is always false when status is "staging".
  const isQueuedStaging =
    props.status === "staging" && props.meetingStatus === "queued";
  const isScheduledForLater =
    isQueuedStaging &&
    props.executeAt != null &&
    new Date(props.executeAt) > new Date(Date.now() + 60_000);
  const isHostAuthExpired =
    isQueuedStaging && props.hostAuthStatus === "expired";
  const isHostNotConnected =
    isQueuedStaging && props.hostAuthStatus === "not_connected";

  const backgroundColor = getGuestStatusBackgroundColor(props.status);

  const handleStatusChange = async (newStatus: GuestStatus) => {
    if (!props.guestId || newStatus === props.status) {
      return;
    }

    try {
      await guestStatusOverride(
        props.meetingId,
        props.guestId,
        newStatus,
        true,
      );
      openSnackbar("Guest status updated");
    } catch {
      openSnackbar("Failed to override guest status");
    }
  };

  const allowedStatuses = canEdit
    ? overrideOptions({
        fromStatus: props.status,
        meetingStage: props.meetingStatus,
      }).map((statusValue: string) => guestStatusOptionFromValue(statusValue))
    : [];

  if (canEdit) {
    return (
      <Select
        value={props.status}
        onChange={(e) => handleStatusChange(e.target.value as GuestStatus)}
        renderValue={(value) => (
          <Box
            sx={{
              textAlign: "center",
              width: "100%",
              fontSize: "12px",
              color: "white",
              lineHeight: "1.2",
            }}
          >
            {GUEST_STATUS_TO_LABEL[value as GuestStatus]}
          </Box>
        )}
        sx={{
          width: "110px",
          minHeight: "36px",
          borderRadius: "4px",
          backgroundColor,
          cursor: "pointer",
          "& .MuiSelect-select": {
            px: "8px !important",
            py: "8px !important",
            textAlign: "center !important",
            lineHeight: "1.2",
            whiteSpace: "normal",
            wordWrap: "break-word",
            display: "flex !important",
            alignItems: "center !important",
            justifyContent: "center !important",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "& .MuiSelect-icon": {
            display: "none",
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 300,
            },
          },
        }}
      >
        {allowedStatuses.map((option: GuestStatusOption | null) => (
          <MenuItem key={option?.value} value={option?.value}>
            {option?.label}
          </MenuItem>
        ))}
      </Select>
    );
  }

  const badgeSx = {
    position: "absolute",
    top: -6,
    right: -6,
    fontSize: 16,
    backgroundColor: "white",
    borderRadius: "50%",
  };

  const statusIcon = isHostAuthExpired ? (
    <Tooltip
      title="Host authentication has expired — meeting is blocked until the host reconnects their email account"
      arrow
    >
      <ErrorOutline sx={{ ...badgeSx, color: "#d32f2f" }} />
    </Tooltip>
  ) : isHostNotConnected ? (
    <Tooltip
      title="Host has not connected their email account — meeting is blocked until the host connects"
      arrow
    >
      <ErrorOutline sx={{ ...badgeSx, color: "#ed6c02" }} />
    </Tooltip>
  ) : isScheduledForLater ? (
    <Tooltip
      title={`Scheduled to be sent on ${new Date(props.executeAt!).toLocaleString()}`}
      arrow
    >
      <ScheduleSend sx={{ ...badgeSx, color: "#1976d2", padding: "1px" }} />
    </Tooltip>
  ) : null;

  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <Box
        sx={{
          width: "110px",
          minHeight: "36px",
          borderRadius: "4px",
          px: "8px",
          py: "8px",
          backgroundColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            fontSize: "12px",
            color: "white",
            textAlign: "center",
            wordWrap: "break-word",
            whiteSpace: "normal",
            lineHeight: "1.2",
          }}
          component="span"
        >
          {GUEST_STATUS_TO_LABEL[props.status]}
        </Typography>
      </Box>
      {statusIcon}
    </Box>
  );
}
